/**
 * Credential type utilities.
 *
 * Display helpers for credential-type tags. Lookups go through the Pinia
 * store (populated from `GET /api/credential-types`) so admin-created types
 * show up without code changes. Callers outside Vue components (plain TS)
 * can pass a resolved record directly.
 *
 * These helpers keep a minimal fallback for the built-in legacy codes so
 * static calls before the store has hydrated (e.g. initial render, tests)
 * don't flash blank tags.
 */

import { useCredentialTypeStore } from '@/stores/credentialTypeStore';
import type { CredentialType } from '@/types/credentialType';

// bb-tag color variant derived from content/credential type code.
// Single source of truth for type → tag color (see BPE-505). Credential types
// (cle/cpe/iapp/ea) plus non-credit learning-object types (skills, microlearning,
// pathway) so every component routes type chips through tagVariant() instead of
// keeping its own map. Any unknown/`default` code falls through to -color-neutral.
// pathway = teal (distinct from skills=purple, which they previously collided with).
// simulation = moss (BPE-700; red and yellow were ruled out because they carry
// state meaning and read wrong on a content-type tag).
//
// Adding a code here is the whole job: every type chip in the app routes through
// tagVariant(), so one entry reaches all of them. Two things have to move with it —
// the type→colour table in docs/UX-Specifications.md, which mirrors this map, and
// the library browse-card accent in styles/components.css, which is Halo's own CSS
// rather than a OneUX token and so can silently disagree with the chip.
const TAG_VARIANT: Record<string, string> = {
  cle: '-color-blue',
  cpe: '-color-green',
  iapp: '-color-lilac',
  ea: '-color-orange',
  skills: '-color-purple',
  microlearning: '-color-pumpkin',
  pathway: '-color-teal',
  simulation: '-color-moss',
};

/** Every variant kind code, in display order. Mirrors backend VariantKind. */
export const VARIANT_KIND_CODES = ['CLE', 'CPE', 'Skills', 'Microlearning', 'Simulation'] as const;

// Variant kind → display label. Static because kinds are a closed set in
// code (adding one is a deploy), so nothing about the label can vary at
// runtime — unlike credential types, which admins create and tagLabel()
// resolves through the store.
const KIND_LABEL: Record<string, string> = {
  cle: 'CLE',
  cpe: 'CPE',
  microlearning: 'Microlearning',
  skills: 'Skills Module',
  pathway: 'Pathway',
  simulation: 'Simulation',
};

// Type → icon (BPE-755). Single source of truth for the icon every type tag
// carries, next to its colour in TAG_VARIANT; the library browse cards use the
// same map. Icons aren't stored on the credential-type record yet, so an
// admin-created credential code falls through to a generic badge.
const TAG_ICON: Record<string, string> = {
  cle: 'gavel',
  cpe: 'account_balance',
  iapp: 'security',
  ea: 'badge',
  skills: 'psychology',
  microlearning: 'play_lesson',
  pathway: 'route',
  simulation: 'record_voice_over',
};

function lookup(code: string | null | undefined): CredentialType | undefined {
  if (!code) return undefined;
  try {
    return useCredentialTypeStore().getByCode(code);
  } catch {
    // Called outside a Pinia-active context (tests, early init) — caller
    // is responsible for passing a resolved record instead.
    return undefined;
  }
}

/** Map a credential type code to its bb-tag color variant class. */
export function tagVariant(code: string | null | undefined): string {
  if (!code) return '';
  return TAG_VARIANT[code.toLowerCase()] ?? '-color-neutral';
}

/** Map a credential type code to its display label. */
export function tagLabel(code: string | null | undefined): string {
  if (!code) return '';
  const record = lookup(code);
  return record?.name ?? code.toUpperCase();
}

/** Map a variant kind code ("CLE", "Skills", …) to its display label. */
export function kindLabel(code: string | null | undefined): string {
  if (!code) return '';
  return KIND_LABEL[code.toLowerCase()] ?? code;
}

// ── Beta kinds ─────────────────────────────────────────────────────────────
// Kinds whose offering is still under beta testing (BPE-745). Learner surfaces
// that name a kind — the sidebar library list, the dashboard library cards, the
// course-detail type tag — say so, to set the expectation before the learner
// opens the content. A product decision, not a per-library setting, so it lives
// here with the other kind facts. Taking a kind out of beta is removing it from
// this set; nothing else has to change.
const BETA_KINDS = new Set(['simulation']);

export const BETA_LABEL = 'Beta';

/** Whether this variant kind is still in beta. */
export function isBetaKind(code: string | null | undefined): boolean {
  if (!code) return false;
  return BETA_KINDS.has(code.toLowerCase());
}

/**
 * The kind's label qualified with its beta stage — "Simulation - Beta" — for
 * the one type tag a course-detail header shows. The stage is part of the tag
 * text rather than a second pill because the header row already carries
 * several tags, and the point is only that the learner reads the two together.
 */
export function kindStageLabel(code: string | null | undefined): string {
  const label = kindLabel(code);
  return isBetaKind(code) ? `${label} - ${BETA_LABEL}` : label;
}

// ── One-chip rows ──────────────────────────────────────────────────────────
// Surfaces that show a single type chip per row (the assignments table, the
// overdue widget) need to pick one type out of a course that may hold several
// kinds. The order below is the shared answer, so those surfaces don't each
// invent one:
//
//   1. the credential the course earns, when the backend implies exactly one
//   2. 'pathway', for a pathway assignment — it holds no kinds of its own
//   3. the course's first kind, which is how a non-credit course gets named
//      and how a dual-credit course (no single implied credential) still
//      lands on a real type
//
// Undefined when there is nothing to go on, so the caller hides the chip. A
// row that can't be named must not fall back to a concrete type: that is how
// the assignments list ended up showing every simulation as CLE.

export interface TypedRow {
  credentialTypeCode?: string | null;
  credentialTypeName?: string | null;
  /** Kind codes the course holds, e.g. `['CLE', 'CPE']` or `['Simulation']`. */
  variantKinds?: readonly string[] | null;
  itemType?: string | null;
}

/** The single type code a one-chip row should wear; undefined if unnameable. */
export function primaryTypeCode(row: TypedRow): string | undefined {
  if (row.credentialTypeCode) return row.credentialTypeCode.toLowerCase();
  if (row.itemType?.toLowerCase() === 'pathway') return 'pathway';
  const kind = row.variantKinds?.find((k) => !!k);
  return kind ? kind.toLowerCase() : undefined;
}

/**
 * The label for that chip: the backend's credential display name when it sent
 * one (it covers admin-created types this code can't enumerate), else the
 * kind's own label. Empty when the row is unnameable, which the caller should
 * treat as "render no chip".
 */
export function primaryTypeLabel(row: TypedRow): string {
  if (row.credentialTypeName) return row.credentialTypeName;
  const code = primaryTypeCode(row);
  return code ? kindLabel(code) : '';
}

// ── Credit vs format kinds ─────────────────────────────────────────────────
// The credit-bearing kinds. Everything else is a format kind: it earns no
// credential, so the credential and taxonomy surfaces don't apply to it.
// Mirrors the backend split, where the credit kinds are exactly those
// inheriting AccreditableVariant.
const CREDIT_KINDS = ['cle', 'cpe'];

/** Whether this set of variant kinds includes a credit-bearing kind. */
export function hasCreditKind(kinds: readonly string[]): boolean {
  return kinds.some((k) => CREDIT_KINDS.includes(k.toLowerCase()));
}

/**
 * Whether a course is format-only: it holds kinds, and none of them earns
 * credit. Such a course hides the credential and taxonomy surfaces
 * (Salesforce, Short Description, Level, Benefits & NASBA, Practice Area &
 * Skills) — they describe credit it cannot earn.
 *
 * An empty set is NOT format-only: a brand-new course with nothing selected
 * yet should show the full form rather than the stripped-down one.
 *
 * Stated as "no credit kind" rather than naming a format kind, so adding one
 * needs no change here (BPE-696 D7). The predicate was previously spelled
 * `isSkillsOnly` in CourseFormView, which only ever *meant* this — Skills was
 * simply the sole authorable format kind at the time.
 */
export function isFormatOnlyCourse(kinds: readonly string[]): boolean {
  return kinds.length > 0 && !hasCreditKind(kinds);
}

// ── Variant-route slugs ────────────────────────────────────────────────────
// The /course/:id/variant/:slug URL segment uses lowercase kind slugs. This
// pair is the single slug↔kind mapping — components never hand-roll it. The
// slug set must stay in lockstep with the route regex in router/index.ts.

const SLUG_TO_KIND: Record<string, string> = {
  cle: 'CLE',
  cpe: 'CPE',
  skills: 'Skills',
  microlearning: 'Microlearning',
  simulation: 'Simulation',
};

/** Map a variant kind code ("CLE", "Skills", …) to its URL slug. */
export function kindToSlug(kind: string | null | undefined): string | undefined {
  if (!kind) return undefined;
  const slug = kind.toLowerCase();
  return slug in SLUG_TO_KIND ? slug : undefined;
}

/** Map a variant-route slug back to its kind code; undefined for garbage. */
export function slugToKind(slug: string | null | undefined): string | undefined {
  if (!slug) return undefined;
  return SLUG_TO_KIND[slug.toLowerCase()];
}

/**
 * Map a type/credential code to the variant kind it lives in: kinds map to
 * themselves; the credential families inside the credit kinds map to their
 * host kind (IAPP → CLE, EA → CPE). Undefined for anything else (e.g.
 * "pathway"), which course links render as the bare kindless URL.
 */
export function kindForTypeCode(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  const c = code.toLowerCase();
  if (c === 'iapp') return 'CLE';
  if (c === 'ea') return 'CPE';
  return SLUG_TO_KIND[c];
}

/**
 * The course-detail location for a course, lensed to a variant kind when one
 * is known. Every course link in the app should be built here so the
 * kind-aware surfaces emit /course/{id}/variant/{slug} and the kindless ones
 * fall back to the bare route (union rendering) — never a hand-built path.
 */
export function courseDetailPath(learningObjectId: number | string, kind?: string | null): string {
  const slug = kindToSlug(kind ?? undefined);
  return slug ? `/course/${learningObjectId}/variant/${slug}` : `/course/${learningObjectId}`;
}

/**
 * Returns the redirect path when a variant-specific page is loaded for a course
 * that doesn't actually have that variant kind. Returns null if the variant is valid.
 */
export function variantMismatchRedirect(
  courseId: number,
  expectedKind: string,
  courseVariantKinds: string[],
  firstAccessibleKind: (kinds: string[]) => string | undefined,
): string | null {
  if (courseVariantKinds.includes(expectedKind)) return null;
  const primaryKind = firstAccessibleKind(courseVariantKinds) ?? courseVariantKinds[0];
  return courseDetailPath(courseId, primaryKind);
}

/** Map a content/credential type code to its Material icon name. */
export function tagIcon(code: string | null | undefined): string {
  if (!code) return '';
  return TAG_ICON[code.toLowerCase()] ?? 'workspace_premium';
}

/**
 * Resolve the primary credential type from a list of accreditation type strings.
 * Preserves the old IAPP/EA > CPE > CLE priority via the store's sortOrder;
 * for legacy seeded data the order matches (CLE=1, CPE=2, IAPP=3, EA=4) so
 * the highest sortOrder wins, mirroring the previous "most specific" rule.
 */
export function resolveCredentialType(accreditationTypes: string[]): string {
  if (!accreditationTypes.length) return 'CLE';
  const upper = accreditationTypes.map(t => t.toUpperCase());
  let best: CredentialType | undefined;
  for (const code of upper) {
    const record = lookup(code);
    if (record && (!best || record.sortOrder > best.sortOrder)) {
      best = record;
    }
  }
  if (best) return best.code.toUpperCase();
  // Fallback: keep old priority when store isn't loaded.
  if (upper.includes('IAPP')) return 'IAPP';
  if (upper.includes('EA')) return 'EA';
  if (upper.includes('CPE')) return 'CPE';
  if (upper.includes('CLE')) return 'CLE';
  return upper[0] ?? 'CLE';
}

/**
 * Unique, sort-ordered credential codes derived from a list of accreditation
 * type strings. A course accredited as both CLE and CPE returns `['cle', 'cpe']`
 * (or whatever the store's sortOrder dictates). Drives the multi-chip row
 * rendering in catalog and dashboard cards.
 */
export function credentialCodesFromAccreditations(accreditationTypes: string[]): string[] {
  if (!accreditationTypes.length) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const t of accreditationTypes) {
    const code = t?.toLowerCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  // Order by store sortOrder when available; fall back to insertion order.
  let store: ReturnType<typeof useCredentialTypeStore> | undefined;
  try {
    store = useCredentialTypeStore();
  } catch {
    return codes;
  }
  return codes.slice().sort((a, b) => {
    const ra = store!.getByCode(a)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const rb = store!.getByCode(b)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return ra - rb || a.localeCompare(b);
  });
}

/**
 * The type tags a course card wears — the one rule every learner surface
 * shares (BPE-557), so the same course is labeled identically in every widget:
 *
 * 1. its accreditation types, one chip each (the caller filters them to the
 *    card's delivery mode first);
 * 2. else its credit kinds (CLE/CPE), for a course not yet accredited;
 * 3. else no credential chip, and its first format kind (Skills,
 *    Microlearning, …) as the fallback kind chip.
 *
 * Never falls back to a concrete credential such as CLE: a course that can't
 * be named renders the card's non-credit state instead.
 */
export function courseTypeTags(
  accreditationTypes: readonly string[] | null | undefined,
  variantKinds: readonly string[] | null | undefined,
): { credentialCodes: string[]; learningObjectTypeCode?: string } {
  const fromAccreditations = credentialCodesFromAccreditations([...(accreditationTypes ?? [])]);
  if (fromAccreditations.length > 0) return { credentialCodes: fromAccreditations };

  const kinds = (variantKinds ?? []).map((k) => k.toLowerCase());
  const creditKinds = credentialCodesFromAccreditations(kinds.filter((k) => CREDIT_KINDS.includes(k)));
  if (creditKinds.length > 0) return { credentialCodes: creditKinds };

  return { credentialCodes: [], learningObjectTypeCode: kinds[0] };
}
